/** Shared catalog of sidebar features and per-action access. */

export type SidebarPortal = 'admin' | 'teacher' | 'supervisor';
export type AccessAction = 'view' | 'add' | 'edit' | 'delete';

export interface SidebarAccessItem {
  id: string;
  label: string;
  icon: string;
  group: string;
  admin: boolean;
  teacher: boolean;
  supervisor: boolean;
  /** Actions that can be granted. Defaults to all four. */
  actions?: AccessAction[];
}

export interface FeaturePermission {
  id: string;
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
}

export type StoredSidebarAccess = Array<string | FeaturePermission> | null | undefined;

export interface SidebarNavItem {
  label: string;
  icon: string;
  route: string;
}

export interface SidebarNavGroup {
  label: string;
  items: SidebarNavItem[];
}

export const ACCESS_ACTIONS: AccessAction[] = ['view', 'add', 'edit', 'delete'];

export const SIDEBAR_ACCESS_ITEMS: SidebarAccessItem[] = [
  { id: 'dashboard', label: 'NAV.DASHBOARD', icon: 'dashboard', group: 'NAV.PLANNING', admin: true, teacher: false, supervisor: false },
  { id: 'attendance_dashboard', label: 'NAV.ATTENDANCE_DASHBOARD', icon: 'fact_check', group: 'NAV.PLANNING', admin: true, teacher: false, supervisor: false },
  { id: 'weekly_plans', label: 'NAV.WEEKLY_PLANS', icon: 'calendar_view_week', group: 'NAV.PLANNING', admin: true, teacher: true, supervisor: true },
  { id: 'attendance', label: 'NAV.ATTENDANCE', icon: 'qr_code_scanner', group: 'NAV.PLANNING', admin: true, teacher: true, supervisor: true, actions: ['view'] },
  { id: 'attendance_manual', label: 'NAV.ATTENDANCE_MANUAL', icon: 'how_to_reg', group: 'NAV.PLANNING', admin: true, teacher: true, supervisor: true, actions: ['view'] },
  { id: 'pickup_board', label: 'NAV.PICKUP_BOARD', icon: 'doorbell', group: 'NAV.PLANNING', admin: true, teacher: true, supervisor: true, actions: ['view'] },
  { id: 'pickup_scan', label: 'NAV.PICKUP_SCAN', icon: 'qr_code_2', group: 'NAV.PLANNING', admin: true, teacher: true, supervisor: true, actions: ['view'] },
  { id: 'weeks', label: 'NAV.WEEKS', icon: 'date_range', group: 'NAV.PLANNING', admin: true, teacher: false, supervisor: false },
  { id: 'majors', label: 'NAV.MAJORS', icon: 'layers', group: 'NAV.ACADEMICS', admin: true, teacher: false, supervisor: false },
  { id: 'subjects', label: 'NAV.SUBJECTS', icon: 'menu_book', group: 'NAV.ACADEMICS', admin: true, teacher: false, supervisor: false },
  { id: 'students', label: 'NAV.STUDENTS', icon: 'school', group: 'NAV.ACADEMICS', admin: true, teacher: false, supervisor: false },
  { id: 'parents', label: 'NAV.PARENTS', icon: 'family_restroom', group: 'NAV.ACADEMICS', admin: true, teacher: false, supervisor: false },
  { id: 'buses', label: 'NAV.BUSES', icon: 'directions_bus', group: 'NAV.ACADEMICS', admin: true, teacher: false, supervisor: false },
  { id: 'academic_years', label: 'NAV.ACADEMIC_YEARS', icon: 'event', group: 'NAV.ACADEMICS', admin: true, teacher: false, supervisor: false },
  { id: 'users', label: 'NAV.USERS', icon: 'group', group: 'NAV.ADMINISTRATION', admin: true, teacher: false, supervisor: false },
  { id: 'announcements', label: 'NAV.ANNOUNCEMENTS', icon: 'campaign', group: 'NAV.ADMINISTRATION', admin: true, teacher: true, supervisor: false },
  { id: 'school', label: 'NAV.SCHOOL', icon: 'school', group: 'NAV.ADMINISTRATION', admin: true, teacher: false, supervisor: false },
];

const ADMIN_ROUTES: Record<string, string> = {
  dashboard: './dashboard',
  attendance_dashboard: './attendance-dashboard',
  weekly_plans: './weekly-plans',
  attendance: './attendance',
  attendance_manual: './attendance-manual',
  pickup_board: './pickup-board',
  pickup_scan: './pickup-scan',
  weeks: './settings/weeks',
  majors: './settings/majors',
  subjects: './settings/subjects',
  students: './settings/students',
  parents: './settings/parents',
  buses: './settings/buses',
  academic_years: './settings/academic-years',
  users: './settings/users',
  announcements: './settings/announcements',
  school: './settings/school',
};

const TEACHER_ROUTES: Record<string, string> = {
  weekly_plans: './weekly-plans',
  attendance: './attendance',
  attendance_manual: './attendance-manual',
  pickup_board: './pickup-board',
  pickup_scan: './pickup-scan',
  announcements: './announcements',
};

const SUPERVISOR_ROUTES: Record<string, string> = {
  weekly_plans: './weekly-plans',
  attendance: './attendance',
  attendance_manual: './attendance-manual',
  pickup_board: './pickup-board',
  pickup_scan: './pickup-scan',
};

export function resolveSidebarPortal(roleName?: string | null, flags?: {
  isAdmin?: boolean;
  isTeacher?: boolean;
  isSupervisor?: boolean;
  isSystemUser?: boolean;
}): SidebarPortal {
  const name = String(roleName || '').trim().toLowerCase();
  if (flags?.isAdmin || flags?.isSystemUser || name === 'admin' || name === 'config') {
    return 'admin';
  }
  if (flags?.isSupervisor || name === 'supervisor') {
    return 'supervisor';
  }
  if (flags?.isTeacher || name === 'teacher') {
    return 'teacher';
  }
  return 'admin';
}

export function roleHasSidebarAccess(item: SidebarAccessItem, portal: SidebarPortal): boolean {
  return !!item[portal];
}

export function emptyPermission(id: string): FeaturePermission {
  return { id, view: false, add: false, edit: false, delete: false };
}

export function fullPermission(id: string): FeaturePermission {
  return { id, view: true, add: true, edit: true, delete: true };
}

export function viewOnlyPermission(id: string): FeaturePermission {
  return { id, view: true, add: false, edit: false, delete: false };
}

export function itemActions(item: SidebarAccessItem): AccessAction[] {
  return item.actions?.length ? item.actions : ACCESS_ACTIONS;
}

export function supportsAction(item: SidebarAccessItem, action: AccessAction): boolean {
  return itemActions(item).includes(action);
}

export function defaultPermissionForPortal(id: string, portal: SidebarPortal): FeaturePermission {
  const item = SIDEBAR_ACCESS_ITEMS.find(entry => entry.id === id);
  const allowed = item ? itemActions(item) : ACCESS_ACTIONS;
  if (!allowed.includes('view')) {
    return emptyPermission(id);
  }
  // Teachers and supervisors can open announcements, but cannot add, edit, or delete unless granted.
  if (portal === 'supervisor' || allowed.length === 1 || (id === 'announcements' && portal !== 'admin')) {
    return viewOnlyPermission(id);
  }
  return {
    id,
    view: true,
    add: allowed.includes('add'),
    edit: allowed.includes('edit'),
    delete: allowed.includes('delete'),
  };
}

export function defaultAccessPermissions(portal: SidebarPortal): FeaturePermission[] {
  return SIDEBAR_ACCESS_ITEMS
    .filter(item => roleHasSidebarAccess(item, portal))
    .map(item => defaultPermissionForPortal(item.id, portal));
}

/** @deprecated Prefer resolveAccessPermissions / resolveGrantedIds */
export function defaultAccessIds(portal: SidebarPortal): string[] {
  return defaultAccessPermissions(portal).map(item => item.id);
}

function normalizeEntry(raw: string | FeaturePermission): FeaturePermission | null {
  if (typeof raw === 'string') {
    const id = raw.trim();
    return id ? clampPermission(fullPermission(id)) : null;
  }
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const id = String(raw.id || '').trim();
  if (!id) {
    return null;
  }
  const view = raw.view === true;
  return clampPermission({
    id,
    view,
    add: view && raw.add === true,
    edit: view && raw.edit === true,
    delete: view && raw.delete === true,
  });
}

function clampPermission(entry: FeaturePermission): FeaturePermission {
  const item = SIDEBAR_ACCESS_ITEMS.find(candidate => candidate.id === entry.id);
  const allowed = item ? itemActions(item) : ACCESS_ACTIONS;
  const view = entry.view === true && allowed.includes('view');
  return {
    id: entry.id,
    view,
    add: view && allowed.includes('add') && entry.add === true,
    edit: view && allowed.includes('edit') && entry.edit === true,
    delete: view && allowed.includes('delete') && entry.delete === true,
  };
}

export function normalizeAccessList(stored?: StoredSidebarAccess): FeaturePermission[] | null {
  if (!Array.isArray(stored)) {
    return null;
  }
  const known = new Set(SIDEBAR_ACCESS_ITEMS.map(item => item.id));
  const byId = new Map<string, FeaturePermission>();
  for (const raw of stored) {
    const entry = normalizeEntry(raw);
    if (!entry || !known.has(entry.id) || !entry.view) {
      continue;
    }
    byId.set(entry.id, entry);
  }
  return SIDEBAR_ACCESS_ITEMS
    .filter(item => byId.has(item.id))
    .map(item => byId.get(item.id)!);
}

export function resolveAccessPermissions(portal: SidebarPortal, stored?: StoredSidebarAccess): FeaturePermission[] {
  return normalizeAccessList(stored) ?? defaultAccessPermissions(portal);
}

export function resolveGrantedIds(portal: SidebarPortal, stored?: StoredSidebarAccess): string[] {
  return resolveAccessPermissions(portal, stored).filter(item => item.view).map(item => item.id);
}

export function permissionMap(portal: SidebarPortal, stored?: StoredSidebarAccess): Map<string, FeaturePermission> {
  return new Map(resolveAccessPermissions(portal, stored).map(item => [item.id, item]));
}

export function canAccess(
  portal: SidebarPortal,
  stored: StoredSidebarAccess,
  featureId: string,
  action: AccessAction = 'view',
): boolean {
  const entry = permissionMap(portal, stored).get(featureId);
  return !!entry?.[action];
}

export function serializeAccessPermissions(entries: FeaturePermission[]): FeaturePermission[] {
  return SIDEBAR_ACCESS_ITEMS
    .map(item => entries.find(entry => entry.id === item.id))
    .filter((entry): entry is FeaturePermission => !!entry && entry.view)
    .map(entry => clampPermission({
      id: entry.id,
      view: true,
      add: entry.add === true,
      edit: entry.edit === true,
      delete: entry.delete === true,
    }));
}

export function accessFingerprint(entries: FeaturePermission[]): string {
  return serializeAccessPermissions(entries)
    .map(entry => `${entry.id}:${entry.view ? 1 : 0}${entry.add ? 1 : 0}${entry.edit ? 1 : 0}${entry.delete ? 1 : 0}`)
    .join('|');
}

export function buildNavGroupsForPortal(portal: SidebarPortal, stored?: StoredSidebarAccess): SidebarNavGroup[] {
  const routes = portal === 'teacher'
    ? TEACHER_ROUTES
    : portal === 'supervisor'
      ? SUPERVISOR_ROUTES
      : ADMIN_ROUTES;
  const granted = new Set(resolveGrantedIds(portal, stored));

  const groups = new Map<string, SidebarNavGroup>();
  for (const item of SIDEBAR_ACCESS_ITEMS) {
    if (!granted.has(item.id)) {
      continue;
    }
    const route = routes[item.id] || ADMIN_ROUTES[item.id];
    if (!route) {
      continue;
    }
    const groupLabel = portal === 'teacher' && item.id === 'announcements'
      ? 'NAV.PLANNING'
      : item.group;
    if (!groups.has(groupLabel)) {
      groups.set(groupLabel, { label: groupLabel, items: [] });
    }
    groups.get(groupLabel)!.items.push({
      label: item.label,
      icon: item.icon,
      route,
    });
  }
  return Array.from(groups.values());
}

export function sidebarAccessGroups(): Array<{ label: string; items: SidebarAccessItem[] }> {
  const groups = new Map<string, SidebarAccessItem[]>();
  for (const item of SIDEBAR_ACCESS_ITEMS) {
    if (!groups.has(item.group)) {
      groups.set(item.group, []);
    }
    groups.get(item.group)!.push(item);
  }
  return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
}

const PATH_ACCESS: Array<[string, string]> = [
  ['settings/users', 'users'],
  ['settings/majors', 'majors'],
  ['settings/subjects', 'subjects'],
  ['settings/classes', 'majors'],
  ['settings/students', 'students'],
  ['settings/parents', 'parents'],
  ['settings/buses', 'buses'],
  ['settings/academic-years', 'academic_years'],
  ['settings/weeks', 'weeks'],
  ['settings/school', 'school'],
  ['settings/announcements', 'announcements'],
  ['announcements', 'announcements'],
  ['attendance-dashboard', 'attendance_dashboard'],
  ['attendance-manual', 'attendance_manual'],
  ['attendance', 'attendance'],
  ['pickup-board', 'pickup_board'],
  ['pickup-scan', 'pickup_scan'],
  ['weekly-plans', 'weekly_plans'],
  ['dashboard', 'dashboard'],
];

export function accessIdForPath(path?: string | null): string | null {
  const value = String(path || '');
  const match = PATH_ACCESS.find(([prefix]) => value === prefix || value.startsWith(`${prefix}/`));
  return match ? match[1] : null;
}

export function actionLabelKey(action: AccessAction): string {
  return `USERS.ACCESS_ACTION_${action.toUpperCase()}`;
}
